import {
    AssessmentDescriptor,
    AssessmentModule,
    AssessmentTemplate,
    AssessmentTemplateDefault,
    AssessmentTemplateDefaultNamespace,
    AssessmentTemplateMetadata,
    AssessmentView,
    PrincipalIdToAssessmentTypeActionMapping,
    PrincipalIdToTemplateActorMapping,
    ShareableTemplateActor,
    UpdateDescriptorInput,
} from '@amzn/aws-assessment-template-management-service-typescript-client';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { TemplateDefaultWithNamespace } from './TemplateDefaults';
import { TemplatePromptViewModel } from './TemplateModels';
import { validateTemplate } from './TemplateUtils';
import { EditableDescriptorKey } from '../../../../api/templateManagement/TemplateManagementClient';
import Constants from '../../../../common/Constants';
import { LoadingStatus } from '../../../../common/RequestUtils';

/* A2T navigation allows users to click on modules and then navigate to the first prompt in that module */
const moduleIdToFirstPromptMap: Map<string, TemplatePromptViewModel> = new Map();
const moduleIdToModuleMap: Map<string, AssessmentModule> = new Map();
/* Key is: `${viewId}${promptId}` */
const viewIdPromptIdToPromptMap: Map<string, TemplatePromptViewModel> = new Map();

export interface CurrentPromptState {
    /** Each view presents the prompts in a different order */
    viewIdToPromptsMap: Map<string, TemplatePromptViewModel[]>;
    /* Number of prompts is used in the status bar*/
    viewIdToNumberOfPrompts: Map<string, number>;
    currentViewId: string;
    currentPromptIndex: number;
    currentPrompt: TemplatePromptViewModel;
}

export const initialCurrentPromptState: CurrentPromptState = {
    viewIdToPromptsMap: null,
    viewIdToNumberOfPrompts: null,
    currentViewId: null,
    currentPromptIndex: null,
    currentPrompt: null,
};

export const initialCurrentTemplateMetadataState: AssessmentTemplateMetadata = {
    templateId: null,
    authorizedAssessmentCreators: null,
    coowners: null,
    createdAt: null,
    descriptors: [],
    isPublishedForType: null,
    isPublishedForVersion: null,
    localeManagers: null,
    owner: null,
    type: null,
    version: null,
};

export interface CurrentTemplateState {
    currentTemplateId: string;
    currentTemplate: AssessmentTemplate;
    currentTemplateMetadata: AssessmentTemplateMetadata;
    templateLocale: string;
    currentlyEditing: boolean;
    currentPromptState: CurrentPromptState;
    /** Key is: `${descriptorId}${locale}`. The values of all editable descriptors should be retrieved from this map, as
     * this is the object that is kept up-to-date with any descriptor edits
     * Although more complex, this indexing is recommended by Redux for complex objects
     * https://redux.js.org/tutorials/essentials/part-6-performance-normalization#normalized-state-structure
     */
    descriptorIdLocaleToEditableDescriptorObject: { [key: string]: AssessmentDescriptor };
    templateLoadingStatus: LoadingStatus;
    templateLoadErrorMessage: string;
    /* To show the status of the requests, maintain a set of in-progress requests */
    editTemplateInProgressRequests: Set<string>;
    /* If any requests fail, this boolean will indicate that a failed autosave status should be shown to the user */
    editTemplateShowError: boolean;
}
export const initialCurrentTemplateState: CurrentTemplateState = {
    currentTemplateId: null,
    currentTemplate: null,
    currentTemplateMetadata: initialCurrentTemplateMetadataState,
    templateLocale: Constants.DEFAULT_LOCALE,
    currentlyEditing: false,
    currentPromptState: initialCurrentPromptState,
    descriptorIdLocaleToEditableDescriptorObject: null,
    templateLoadingStatus: LoadingStatus.NotLoaded,
    templateLoadErrorMessage: '',
    editTemplateInProgressRequests: null,
    editTemplateShowError: false,
};

export const currentTemplateSlice = createSlice({
    name: 'currentTemplateSlice',
    initialState: initialCurrentTemplateState,
    reducers: {
        /**
         * Call once the new template is received
         * @param state current Redux state
         * @param action the Redux payload, with the template to load
         */
        loadTemplateSuccess: (state, action: PayloadAction<AssessmentTemplate>) => {
            state.currentTemplateId = action.payload.templateId;
            state.currentTemplate = action.payload;

            // Validate the template. Allow the dispatcher to handle any errors
            validateTemplate(state.currentTemplate);

            const viewIdToPromptsMap: Map<string, TemplatePromptViewModel[]> = new Map();
            const viewIdToNumberOfPrompts: Map<string, number> = new Map();
            const descriptorIdLocaleToEditableDescriptorObject: { [key: string]: AssessmentDescriptor } = {};
            /**
             * Pre-order traversal of the view. E.g. view -> module -> prompts in module -> childModule -> childModule -> prompt. Once a prompt is found:
             * 1. Add it to the `prompts` array (specific to this view)
             * 2. Add the all parent module ids to the `moduleIdToFirstPromptIndexMap` map
             * @param view the view to process
             */
            function addPromptsInView(view: AssessmentView): void {
                const prompts: TemplatePromptViewModel[] = [];
                let numberOfPromptsInView = 0;

                // This stack contains all parent modules of the prompt
                const moduleIdsStack: string[] = [];

                /**
                 * Given a module, add all its prompts to the `prompts` array
                 * Additionally checks each child module
                 * @param module the current module
                 */
                function addPromptsInModule(module: AssessmentModule): void {
                    moduleIdsStack.push(module.moduleId);

                    // Add each of the prompts to the prompts array. If we're dealing with the first prompt,
                    // add it to the `moduleIdToFirstPromptIndexMap`
                    module.prompts.forEach((prompt) => {
                        const promptViewModel: TemplatePromptViewModel = {
                            ...prompt,
                            containingModule: module,
                        };
                        prompts.push(promptViewModel);

                        // For each module among the prompt's ancestors, set the corresponding prompt
                        // But if a prompt has already been set, skip it (so we only have first prompts set)
                        moduleIdsStack.forEach((moduleId) => {
                            if (!moduleIdToFirstPromptMap.has(moduleId)) {
                                moduleIdToFirstPromptMap.set(moduleId, promptViewModel);
                            }
                        });

                        // Increment `numberOfPromptsInView`
                        numberOfPromptsInView++;

                        // Since prompt descriptors are editable, add them here
                        prompt.descriptors.forEach((descriptor) => {
                            descriptorIdLocaleToEditableDescriptorObject[`${descriptor.descriptorId}${descriptor.locale}`] = descriptor;
                        });

                        // Add any rating guide descriptors too
                        if (prompt.ratingGuide) {
                            Object.values(prompt.ratingGuide).forEach((value) => {
                                // Ignore __typename
                                if (value === 'RatingGuide') {
                                    return;
                                }

                                value?.forEach((ratingGuideDescriptor) => {
                                    descriptorIdLocaleToEditableDescriptorObject[
                                        `${ratingGuideDescriptor.descriptorId}${ratingGuideDescriptor.locale}`
                                    ] = ratingGuideDescriptor;
                                });
                            });
                        }

                        // Add selections to the editable descriptor object
                        prompt.selectionDescription?.forEach((descriptor) => {
                            descriptorIdLocaleToEditableDescriptorObject[`${descriptor.descriptorId}${descriptor.locale}`] = descriptor;
                        });
                        prompt.selections?.forEach((selection) => {
                            selection.selectionLabel.forEach((selectionDescriptor) => {
                                descriptorIdLocaleToEditableDescriptorObject[`${selectionDescriptor.descriptorId}${selectionDescriptor.locale}`] =
                                    selectionDescriptor;
                            });
                        });

                        viewIdPromptIdToPromptMap.set(`${view.viewId}${prompt.promptId}`, promptViewModel);
                    });

                    module.childModules.forEach(addPromptsInModule);

                    // So we don't have to iterate through the template tree, allow easy retrieval of the module
                    // given the module ID
                    moduleIdToModuleMap.set(module.moduleId, module);

                    // Module names and help context are editable
                    module.descriptors.forEach((descriptor) => {
                        descriptorIdLocaleToEditableDescriptorObject[`${descriptor.descriptorId}${descriptor.locale}`] = descriptor;
                    });

                    moduleIdsStack.pop();
                }

                view.modules.forEach(addPromptsInModule);

                viewIdToPromptsMap.set(view.viewId, prompts);
                viewIdToNumberOfPrompts.set(view.viewId, numberOfPromptsInView);
            }

            state.currentTemplate.views.forEach(addPromptsInView);

            // Template descriptor/delivery guidance is editable
            state.currentTemplate.descriptors.forEach((descriptor) => {
                descriptorIdLocaleToEditableDescriptorObject[`${descriptor.descriptorId}${descriptor.locale}`] = descriptor;
            });
            state.currentTemplate.deliveryGuidance.forEach((descriptor) => {
                descriptorIdLocaleToEditableDescriptorObject[`${descriptor.descriptorId}${descriptor.locale}`] = descriptor;
            });

            const firstViewId = state.currentTemplate.views[0].viewId;
            // Also get the module containing the first prompt
            let tempChildModule: AssessmentModule = state.currentTemplate.views[0].modules[0];
            while (tempChildModule.prompts.length === 0) {
                tempChildModule = tempChildModule.childModules[0];
            }

            state.currentPromptState = {
                currentPromptIndex: 0,
                currentViewId: firstViewId,
                currentPrompt: viewIdToPromptsMap.get(firstViewId)[0],
                viewIdToPromptsMap,
                viewIdToNumberOfPrompts: viewIdToNumberOfPrompts,
            };

            state.descriptorIdLocaleToEditableDescriptorObject = descriptorIdLocaleToEditableDescriptorObject;

            state.editTemplateInProgressRequests = new Set();
        },
        /**
         * Loads the template metadata, retrieved by calling the listEditableTemplates API
         * @param state current Redux state
         * @param action the template metadata
         */
        loadTemplateMetadata: (state, action: PayloadAction<AssessmentTemplateMetadata>) => {
            state.currentTemplateMetadata = action.payload;
        },
        /**
         * Indicate that the template retrieval request has been made. Also resets any error message
         * @param state current Redux state
         */
        beginLoadingTemplate: (state) => {
            state.templateLoadingStatus = LoadingStatus.Loading;
            state.templateLoadErrorMessage = initialCurrentTemplateState.templateLoadErrorMessage;
        },
        /**
         * Indicate that the template and its metadata have finished loading
         * @param state current Redux state
         */
        completeLoadingTemplate: (state) => {
            state.templateLoadingStatus = LoadingStatus.Loaded;
        },
        /**
         * Unloads the current template. Used when loading a new template
         * @param state current Redux state
         */
        unloadCurrentTemplate: (state) => {
            moduleIdToFirstPromptMap.clear();
            moduleIdToModuleMap.clear();
            viewIdPromptIdToPromptMap.clear();
            Object.assign(state, initialCurrentTemplateState);
        },
        /**
         * Called when a template fails to load
         * @param state current Redux state
         * @param action parameter with error message
         */
        loadTemplateFailure: (state, { payload: errorMessage }: PayloadAction<string>) => {
            currentTemplateSlice.caseReducers.unloadCurrentTemplate(state);

            state.templateLoadErrorMessage = errorMessage;
            state.templateLoadingStatus = LoadingStatus.FailedToLoad;
        },
        /**
         * Jump to the first prompt in the specified module
         * @param state current Redux state
         * @param action  the view/module to jump to
         */
        jumpToModule: (state, action: PayloadAction<{ viewId: string; moduleId: string }>) => {
            state.currentPromptState.currentViewId = action.payload.viewId;
            state.currentPromptState.currentPrompt = moduleIdToFirstPromptMap.get(action.payload.moduleId);

            const currentPrompts = state.currentPromptState.viewIdToPromptsMap.get(state.currentPromptState.currentViewId);
            state.currentPromptState.currentPromptIndex =
                currentPrompts.findIndex((prompt) => prompt.promptId === state.currentPromptState.currentPrompt.promptId) || 0;
        },
        /**
         * Jump to the first prompt in the specified module
         * @param state current Redux state
         * @param action  the view/promptId to jump to
         */
        jumpToPrompt: (state, action: PayloadAction<{ viewId: string; promptId: string }>) => {
            state.currentPromptState.currentViewId = action.payload.viewId;
            const currentPrompts = state.currentPromptState.viewIdToPromptsMap.get(state.currentPromptState.currentViewId);

            // If the prompt can't be found, go to the first prompt
            state.currentPromptState.currentPromptIndex = currentPrompts.findIndex((prompt) => prompt.promptId === action.payload.promptId) || 0;
            state.currentPromptState.currentPrompt = currentPrompts[state.currentPromptState.currentPromptIndex];
        },
        /**
         * Go to the next prompt within the same view
         * @param state current Redux state
         */
        goToNextPrompt: (state) => {
            const currentPrompts = state.currentPromptState.viewIdToPromptsMap.get(state.currentPromptState.currentViewId);
            if (state.currentPromptState.currentPromptIndex === currentPrompts.length - 1) {
                return;
            }
            state.currentPromptState.currentPromptIndex++;
            state.currentPromptState.currentPrompt = currentPrompts[state.currentPromptState.currentPromptIndex];
        },
        /**
         * Go to the previous prompt within the same view
         * @param state
         */
        goToPreviousPrompt: (state) => {
            if (state.currentPromptState.currentPromptIndex === 0) {
                return;
            }
            state.currentPromptState.currentPromptIndex--;
            state.currentPromptState.currentPrompt = state.currentPromptState.viewIdToPromptsMap.get(state.currentPromptState.currentViewId)[
                state.currentPromptState.currentPromptIndex
            ];
        },
        /**
         * Sets the locale for the template. Not used in the reducer, but helpful for components to know/share
         * @param state current Redux state
         * @param action the new locale
         */
        setTemplateLocale: (state, action: PayloadAction<string>) => {
            state.templateLocale = action.payload;
        },
        /**
         * Sets whether the user is viewing or editing a template
         * @param state current Redux state
         * @param action the new value of `currentlyEditing`
         */
        setCurrentlyEditing: (state, action: PayloadAction<boolean>) => {
            state.currentlyEditing = action.payload;
        },
        /**
         * Updates a descriptor in the current template (only in the Redux store)
         * @param state current Redux state
         * @param action the descriptor update
         */
        updateDescriptor: (state, action: PayloadAction<{ input: UpdateDescriptorInput; keyToUpdate: EditableDescriptorKey }>) => {
            const { input, keyToUpdate } = action.payload;

            const descriptor = state.descriptorIdLocaleToEditableDescriptorObject[`${input.descriptorId}${input.locale}`];
            if (descriptor) {
                descriptor[keyToUpdate] = input[keyToUpdate];
            }
        },
        /**
         * Updates the locally stored template permissions
         * @param state current Redux state
         * @param action the added and removed permissions
         */
        updateTemplatePermissions: (
            state,
            action: PayloadAction<{ addedPermissions: PrincipalIdToTemplateActorMapping[]; removedPermissions: PrincipalIdToTemplateActorMapping[] }>
        ) => {
            const currentCoowners = state.currentTemplateMetadata.coowners;
            const currentLocaleManagers = state.currentTemplateMetadata.localeManagers;

            const coownersToAdd = action.payload.addedPermissions.filter((permission) => permission.templateActor === ShareableTemplateActor.CoOwner);
            const coownersToRemove = action.payload.removedPermissions.filter(
                (permission) => permission.templateActor === ShareableTemplateActor.CoOwner
            );
            const localeManagersToAdd = action.payload.addedPermissions.filter(
                (permission) => permission.templateActor === ShareableTemplateActor.LocaleManager
            );
            const localeManagersToRemove = action.payload.removedPermissions.filter(
                (permission) => permission.templateActor === ShareableTemplateActor.LocaleManager
            );

            const newCoowners = currentCoowners
                .filter((coowner) => !coownersToRemove.some((permission) => permission.principalId === coowner))
                .concat(coownersToAdd.map(({ principalId }) => principalId));

            const updatedLocaleManagers = currentLocaleManagers;
            // For each addition, update the locale managers
            localeManagersToAdd.forEach(({ principalId, locale }) => {
                const localeManagersForLocale = updatedLocaleManagers.find(({ locale: existingLocale }) => existingLocale === locale);
                // Array with permissions for this locale already exists
                if (localeManagersForLocale) {
                    localeManagersForLocale.localeManagers.push(principalId);
                } else {
                    // Create permissions array for this locale
                    updatedLocaleManagers.push({ locale, localeManagers: [principalId] });
                }
            });
            // For each removal, update
            localeManagersToRemove.forEach(({ principalId, locale }) => {
                const localeManagersForLocale = updatedLocaleManagers.find(({ locale: existingLocale }) => existingLocale === locale);
                if (localeManagersForLocale) {
                    localeManagersForLocale.localeManagers = localeManagersForLocale.localeManagers.filter(
                        (existingPrincipalId) => existingPrincipalId !== principalId
                    );
                }
            });

            state.currentTemplateMetadata.coowners = newCoowners;
            state.currentTemplateMetadata.localeManagers = updatedLocaleManagers;
        },
        /**
         * Updates the locally stored assessment type permissions
         * @param state current Redux state
         * @param action the added and removed permissions
         */
        updateAssessmentTypePermissions: (
            state,
            action: PayloadAction<{
                addedPermissions: PrincipalIdToAssessmentTypeActionMapping[];
                removedPermissions: PrincipalIdToAssessmentTypeActionMapping[];
            }>
        ) => {
            const currentAssessmentCreators = state.currentTemplateMetadata.authorizedAssessmentCreators;

            const assessmentTypePermissionsToAdd: string[] = action.payload.addedPermissions.map(({ principalId }) => principalId);
            const assessmentTypePermissionsToRemove: PrincipalIdToAssessmentTypeActionMapping[] = action.payload.removedPermissions;

            const newAssessmentCreators = currentAssessmentCreators
                .filter(
                    (authorizedPrincipal) =>
                        !assessmentTypePermissionsToRemove.some((removedPermission) => removedPermission.principalId === authorizedPrincipal)
                )
                .concat(assessmentTypePermissionsToAdd);

            state.currentTemplateMetadata.authorizedAssessmentCreators = newAssessmentCreators;
        },
        /**
         * Updates a default
         * @param state current Redux state
         * @param action the modified default, as well as the containing namespace
         */
        createOrUpdateDefault: (state, action: PayloadAction<TemplateDefaultWithNamespace>) => {
            const { key, namespace, value, valueType, descriptors } = action.payload;

            // Get the descriptor objects
            const descriptorObjects: AssessmentDescriptor[] | undefined = descriptors?.map(
                (descriptorId) => state.descriptorIdLocaleToEditableDescriptorObject[`${descriptorId}${state.templateLocale}`]
            );

            const defaultNamespace: AssessmentTemplateDefaultNamespace | undefined = state.currentTemplate?.config.defaultNamespaces.find(
                (templateNamespace) => templateNamespace.namespace === namespace
            );

            // If the namespace doesn't already exist, create it
            if (!defaultNamespace) {
                state.currentTemplate.config.defaultNamespaces.push({
                    namespace,
                    defaults: [{ key, value, valueType, descriptors: descriptorObjects }],
                });
                return;
            }

            const templateDefault: AssessmentTemplateDefault | undefined = state.currentTemplate?.config.defaultNamespaces
                .find((templateNamespace) => templateNamespace.namespace === namespace)
                ?.defaults.find((templateDefault) => templateDefault.key === key);

            // If the default doesn't already exist, create it
            if (!templateDefault) {
                defaultNamespace.defaults.push({ key, value, valueType, descriptors: descriptorObjects });
                return;
            }

            templateDefault.value = value;
            templateDefault.descriptors = descriptorObjects;
        },
        /**
         * Adds a request to the currently in-progress requests set
         * @param state current Redux state
         * @param action a UUID identifying the request
         */
        addInProgressRequest: (state, action: PayloadAction<string>) => {
            state.editTemplateInProgressRequests.add(action.payload);
        },
        /**
         * Indicates that a request has completed. If the request failed, set `didRequestFail` to `true`
         * @param state current Redux state
         * @param action the ID of the request that just completed, and if that request failed
         */
        removeInProgressRequest: (state, action: PayloadAction<{ requestId: string; didRequestFail?: boolean }>) => {
            state.editTemplateInProgressRequests.delete(action.payload.requestId);
            if (action.payload.didRequestFail) {
                state.editTemplateShowError = true;
            }
        },
    },
});

/**
 * Returns any module in the template, given its ID
 * @param moduleId the module ID
 * @returns the corresponding module
 */
export const getModuleFromModuleId = (moduleId: string): AssessmentModule | undefined => {
    return moduleIdToModuleMap.get(moduleId);
};

/**
 * Returns the first prompt in the specified module
 * @param moduleId the ID of the module
 * @returns the first prompt in that module (or its children)
 */
export const getFirstPromptInModule = (moduleId: string): TemplatePromptViewModel => {
    return moduleIdToFirstPromptMap.get(moduleId);
};

/**
 * Returns the prompt with the given ID
 * @param viewId the view within which the prompt exists
 * @param promptId the prompt ID to retrieve
 * @returns the corresponding prompt
 */
export const getPromptFromViewIdPromptId = (viewId: string, promptId: string): TemplatePromptViewModel => {
    return viewIdPromptIdToPromptMap.get(`${viewId}${promptId}`);
};

/**
 * The timeline in the facilitate page shows the modules that are at the same level as
 * the prompt's parent module. This function returns the modules that are at that same level
 * @param promptId the current prompt
 * @returns the modules that are at the same level as the prompt's parent module
 */
export const getSiblingModulesOfPrompt = (viewId: string, promptId: string): AssessmentModule[] => {
    const currentPrompt: TemplatePromptViewModel = getPromptFromViewIdPromptId(viewId, promptId);

    // Can't find prompt --> return empty list
    if (!currentPrompt) {
        return [];
    }

    // There may be no parent module
    const parentModuleId: string = currentPrompt.containingModule.parentModule?.moduleId || '';
    const parentModuleOfSiblings: AssessmentModule | undefined = moduleIdToModuleMap.get(parentModuleId);
    return parentModuleOfSiblings?.childModules || [];
};

export const {
    loadTemplateSuccess,
    loadTemplateMetadata,
    beginLoadingTemplate,
    completeLoadingTemplate,
    unloadCurrentTemplate,
    loadTemplateFailure,
    jumpToModule,
    jumpToPrompt,
    goToNextPrompt,
    goToPreviousPrompt,
    setTemplateLocale,
    setCurrentlyEditing,
    updateDescriptor,
    updateTemplatePermissions,
    updateAssessmentTypePermissions,
    createOrUpdateDefault,
    addInProgressRequest,
    removeInProgressRequest,
} = currentTemplateSlice.actions;

export default currentTemplateSlice.reducer;
